import { useEffect, useRef, useState } from "react";
import { Send, X, Loader2, MessageCircle } from "lucide-react";
import { sendChat, endChatDemo } from "../lib/api";

/**
 * Bolla chat di prova. Stessa AI della voce (stesso system prompt).
 * Si presenta come il widget che il cliente potrà mettere sul suo sito.
 * Limite 10 turni utente (anti-abuse lato backend).
 */
export default function ChatTryout({ sessionId, companyName, greeting, onClose }) {
  const [messages, setMessages] = useState(() => [
    {
      role: "assistant",
      content:
        greeting ||
        `Ciao! Sono l'assistente di ${companyName}. Come posso aiutarti?`,
    },
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [limitReached, setLimitReached] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const mountedRef = useRef(true);
  const chatEndSentRef = useRef(false);
  const hasUserMsgRef = useRef(false);

  // Trigger affidabile della chiusura chat: usa sendBeacon su pagehide
  // (sopravvive a tab close / navigazione) con fallback fetch+keepalive.
  // Il backend è idempotente (unique index session_id+channel), quindi
  // chiamarlo più volte non genera duplicati.
  const triggerChatEnd = () => {
    if (!hasUserMsgRef.current || chatEndSentRef.current) return;
    chatEndSentRef.current = true;
    try {
      const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
      const url = `${base}/api/demo/${sessionId}/chat-end`;
      if (typeof navigator !== "undefined" && navigator.sendBeacon) {
        const blob = new Blob([""], { type: "application/json" });
        const ok = navigator.sendBeacon(url, blob);
        if (ok) return;
      }
      fetch(url, { method: "POST", keepalive: true }).catch(() => {});
    } catch {
      endChatDemo(sessionId).catch(() => {});
    }
  };

  useEffect(() => {
    const onPageHide = () => triggerChatEnd();
    window.addEventListener("pagehide", onPageHide);
    return () => {
      window.removeEventListener("pagehide", onPageHide);
      mountedRef.current = false;
      // Unmount (es. utente chiude la chat o cambia pagina dentro l'app)
      triggerChatEnd();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const handleClose = () => {
    triggerChatEnd();
    onClose?.();
  };

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const userTurnsUsed = messages.filter((m) => m.role === "user").length;
  const turnsLeft = Math.max(0, 10 - userTurnsUsed);
  const inputDisabled = sending || limitReached || turnsLeft <= 0;

  const safeSet = (fn) => {
    if (mountedRef.current) fn();
  };

  const handleSend = async () => {
    const text = input.trim();
    if (!text || sending || limitReached || turnsLeft <= 0) return;
    setError("");
    setInput("");
    // Append ottimistico
    setMessages((prev) => [...prev, { role: "user", content: text }]);
    hasUserMsgRef.current = true;
    setSending(true);
    try {
      const res = await sendChat(sessionId, text, { textOnly: true });
      safeSet(() =>
        setMessages((prev) => [...prev, { role: "assistant", content: res.text }])
      );
    } catch (e) {
      const status = e?.response?.status;
      const detail =
        e?.response?.data?.detail || "Errore di rete. Riprova tra un secondo.";
      // Rollback dell'append ottimistico se la chiamata è fallita
      safeSet(() =>
        setMessages((prev) =>
          prev[prev.length - 1]?.role === "user" && prev[prev.length - 1]?.content === text
            ? prev.slice(0, -1)
            : prev
        )
      );
      // Ripristina il testo nell'input così l'utente non lo perde
      safeSet(() => setInput(text));
      safeSet(() => setError(detail));
      if (status === 429) {
        safeSet(() => setLimitReached(true));
      }
    } finally {
      safeSet(() => setSending(false));
    }
  };

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center sm:justify-end p-0 sm:p-6"
      data-testid="chat-tryout-modal"
    >
      <div className="bg-white w-full sm:max-w-md h-[85vh] sm:h-[600px] sm:rounded-2xl shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-br from-brand-olive to-brand-olive/80 text-white px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center shrink-0">
              <MessageCircle className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <p className="font-heading text-base leading-tight truncate">
                {companyName}
              </p>
              <p className="text-xs text-white/80">
                Chat in omaggio · prova online
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="text-white/80 hover:text-white transition shrink-0 p-1"
            aria-label="Chiudi chat"
            data-testid="chat-tryout-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Messaggi */}
        <div
          ref={scrollRef}
          className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-brand-bg2"
        >
          {messages.map((m, i) => (
            <div
              key={i}
              className={`flex ${
                m.role === "user" ? "justify-end" : "justify-start"
              }`}
            >
              <div
                className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                  m.role === "user"
                    ? "bg-brand-olive text-white rounded-br-sm"
                    : "bg-white text-brand-ink border border-brand-line rounded-bl-sm"
                }`}
                data-testid={`chat-msg-${m.role}-${i}`}
              >
                {m.content}
              </div>
            </div>
          ))}
          {sending && (
            <div className="flex justify-start">
              <div className="bg-white border border-brand-line rounded-2xl rounded-bl-sm px-4 py-3 flex gap-1.5">
                <span className="w-2 h-2 bg-brand-inkSoft/50 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                <span className="w-2 h-2 bg-brand-inkSoft/50 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                <span className="w-2 h-2 bg-brand-inkSoft/50 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
              </div>
            </div>
          )}
          {error && !limitReached && (
            <div className="bg-red-50 border border-red-200 rounded-xl px-3 py-2 text-xs text-red-800">
              {error}
            </div>
          )}
        </div>

        {/* Input */}
        <div className="border-t border-brand-line bg-white px-3 py-3">
          {limitReached || turnsLeft <= 0 ? (
            <p className="text-center text-xs text-brand-inkSoft py-2">
              Hai usato i 10 messaggi di prova. Per attivare la chat sul tuo sito
              scrivi a <span className="font-semibold">robs@24vox.it</span>.
            </p>
          ) : (
            <>
              <div className="flex gap-2 items-end">
                <textarea
                  ref={inputRef}
                  rows={1}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKey}
                  placeholder="Scrivi un messaggio..."
                  disabled={inputDisabled}
                  className="flex-1 resize-none rounded-xl border border-brand-line px-3 py-2 text-sm focus:outline-none focus:border-brand-olive disabled:bg-brand-bg2 max-h-28"
                  data-testid="chat-tryout-input"
                />
                <button
                  onClick={handleSend}
                  disabled={inputDisabled || !input.trim()}
                  className="bg-brand-olive hover:bg-brand-olive/90 disabled:opacity-40 text-white rounded-xl w-10 h-10 flex items-center justify-center transition shrink-0"
                  data-testid="chat-tryout-send"
                  aria-label="Invia"
                >
                  {sending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                </button>
              </div>
              <p className="mt-2 text-[10px] text-brand-inkSoft text-center">
                {turnsLeft} {turnsLeft === 1 ? "messaggio rimasto" : "messaggi rimasti"} · stessa AI della telefonata
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
